import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import { addNewAssetType } from '../../../actions/inventory';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { useForm } from 'react-hook-form';
import Checkbox from '../../../common/Form/Checkbox';
import Customer from '../../../components/Inputs/Customer';
import { canManageOtherOrgs } from '../../../utils/access';

const AddAssetTypes = ({ org_id = undefined }) => {
    const formName = 'addAssetsTypeForm';
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading);
    const { newLoggedUserData } = useSelector((state) => state.auth);
    const defaultValues = {};
    const attributesContainerRef = useRef(null);
    const managerOtherOrgs = canManageOtherOrgs(newLoggedUserData.actions) && !org_id;

    const submit = (formValues) => {
        const additional_fields = [];
        const standardAttributes = ['serial_number', 'partcode', 'make', 'model'];
        standardAttributes.forEach(attr => {
            if (formValues?.[attr]) {
                additional_fields.push({
                    key: attr,
                    label: attr.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                    is_required: true,
                });
            }
        });

        if (formValues?.customAttributes?.length) {
            formValues.customAttributes.forEach(attribute => {
                if (attribute?.name) {
                    additional_fields.push({
                        key: attribute.name,
                        label: attribute.name,
                        is_required: attribute.is_required,
                    });
                }
            });
        }

        const payload = {
            title: formValues?.title,
            org_id: formValues?.customer_id?.value || newLoggedUserData?.org?.id,
        };

        if (org_id) {
            payload.org_id = org_id;
        }

        if (additional_fields.length > 0) {
            payload.additional_fields = additional_fields;
        }

        dispatch(addNewAssetType({ payload, formName, append: true }));
    };

    const closeModal = () => dispatch(closeModalAction());

    const methods = useForm({
        defaultValues: {
            serial_number: true,
            partcode: false,
            make: false,
            model: false,
        },
        mode: 'all'
    });

    const addCustomAttribute = () => {
        const currentAttributes = methods.getValues('customAttributes') || [];
        methods.setValue('customAttributes', [
            ...currentAttributes,
            { name: '', is_required: true }
        ]);

        setTimeout(() => {
            if (attributesContainerRef.current) {
                attributesContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        }, 100);
    };

    const removeCustomAttribute = (index) => {
        const currentAttributes = methods.getValues('customAttributes');
        methods.setValue(
            'customAttributes',
            currentAttributes.filter((_, i) => i !== index)
        );
    };

    return (
        <div className='w-full h-full overflow-hidden'>
            <Form
                className='w-full'
                onSubmit={submit}
                onCancel={closeModal}
                defaultValues={defaultValues}
                submitButtonText={'Add'}
                showSubmit
                submitContainerClassName='flex gap-2 justify-end py-4 px-4'
                isLoading={formButtonLoading[formName]}
                methods={methods}
            >
                <div className='p-3'>
                    <div className='flex gap-2'>
                        <Input className='w-full' label='Asset Name' placeholder='Asset Name' required name='title' type={'text'} />
                        {managerOtherOrgs && <Customer className='w-full' label='Customer' name='customer_id' />}
                    </div>

                    <div className="mt-4">
                        <p className="text-lg font-semibold mb-2">Select Attributes:</p>
                        <div className="flex flex-wrap gap-4">
                            <Checkbox id='serial_number' label='Serial Number' name='serial_number' />
                            <Checkbox id='partcode' label='Partcode' name='partcode' />
                            <Checkbox id='make' label='Make' name='make' />
                            <Checkbox id='model' label='Model' name='model' />
                        </div>
                    </div>

                    <div className="mt-2 w-full">
                        <div className="flex flex-col w-full gap-3" >
                            {methods.watch('customAttributes')?.map((attribute, index) => (
                                <div key={index} className="flex items-center gap-2 w-full">
                                    <div className="flex-grow">
                                        <Input
                                            label={`Custom Attribute ${index + 1}`}
                                            placeholder="Enter attribute name"
                                            name={`customAttributes.${index}.name`}
                                            type="text"
                                            className='w-full'
                                        />
                                    </div>
                                    <div className="mt-6 flex items-center gap-2">
                                        <Checkbox
                                            id={`customAttributes.${index}.is_requzired`}
                                            label='Required'
                                            name={`customAttributes.${index}.is_required`}
                                        />
                                        <button
                                            type="button"
                                            className="text-red-500"
                                            onClick={() => removeCustomAttribute(index)}
                                        >
                                            <span className="material-icons text-[14px]">delete</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='flex items-center' ref={attributesContainerRef}>
                            <button
                                type="button"
                                className="flex items-center gap-1 text-scogoorange my-2"
                                onClick={addCustomAttribute}
                            >
                                <span className="material-icons text-[14px] font-bold">add</span>
                                <span className='text-[12px]'>Add Custom Attribute</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    );
};

export default AddAssetTypes;
